import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface AuditLog {
  id: string;
  adminId: string;
  action: string;
  targetType: 'user' | 'lawsuit' | 'document' | 'system';
  targetId?: string;
  changes?: Record<string, any>;
  ipAddress?: string;
  timestamp: Date;
  admin?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const AuditLogPage: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      let url = `/api/admin/audit-logs?page=${page}&limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (targetTypeFilter) url += `&targetType=${targetTypeFilter}`;

      const response = await axios.get<{
        data: AuditLog[];
        pagination: { total: number; pages: number };
      }>(url);

      setLogs(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, targetTypeFilter]);

  const getActionBadgeColor = (action: string) => {
    if (action.includes('delete')) return 'bg-red-100 text-red-800';
    if (action.includes('ban')) return 'bg-yellow-100 text-yellow-800';
    if (action.includes('update')) return 'bg-blue-100 text-blue-800';
    if (action.includes('export')) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (isLoading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('admin.auditLog', 'Audit Log')}
        </h2>
        <div className="text-sm text-gray-500">
          {t('admin.totalLogs', 'Total Logs')}: {total}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.filterByAction', 'Filter by Action')}
            </label>
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-white text-gray-900 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
            >
              <option value="">{t('admin.allActions', 'All Actions')}</option>
              <option value="soft_delete_user">Soft Delete User</option>
              <option value="soft_delete_lawsuit">Soft Delete Lawsuit</option>
              <option value="ban_user">Ban User</option>
              <option value="unban_user">Unban User</option>
              <option value="update_admin_status">Update Admin Status</option>
              <option value="update_lawsuit_status">Update Lawsuit Status</option>
              <option value="export_lawsuits_csv">Export CSV</option>
              <option value="export_lawsuits_pdf">Export PDF</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.filterByTargetType', 'Filter by Target Type')}
            </label>
            <select
              value={targetTypeFilter}
              onChange={(e) => {
                setTargetTypeFilter(e.target.value);
                setPage(1);
              }}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-white text-gray-900 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
            >
              <option value="">{t('admin.allTypes', 'All Types')}</option>
              <option value="user">User</option>
              <option value="lawsuit">Lawsuit</option>
              <option value="document">Document</option>
              <option value="system">System</option>
            </select>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.timestamp', 'Timestamp')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.admin', 'Admin')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.action', 'Action')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.target', 'Target')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.details', 'Details')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {log.admin ? `${log.admin.firstName} ${log.admin.lastName}` : log.adminId}
                  </div>
                  {log.admin && (
                    <div className="text-sm text-gray-500">{log.admin.email}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionBadgeColor(
                      log.action
                    )}`}
                  >
                    {log.action.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{log.targetType}</div>
                  {log.targetId && (
                    <div className="text-xs text-gray-500">{log.targetId.substring(0, 8)}...</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {log.changes && (
                    <div className="text-xs text-gray-500 max-w-md truncate">
                      {JSON.stringify(log.changes)}
                    </div>
                  )}
                  {log.ipAddress && (
                    <div className="text-xs text-gray-400 mt-1">IP: {log.ipAddress}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {t('pagination.previous', 'Previous')}
          </button>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {t('pagination.next', 'Next')}
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              {t('pagination.showing', 'Showing')}{' '}
              <span className="font-medium">{(page - 1) * limit + 1}</span>{' '}
              {t('pagination.to', 'to')}{' '}
              <span className="font-medium">{Math.min(page * limit, total)}</span>{' '}
              {t('pagination.of', 'of')}{' '}
              <span className="font-medium">{total}</span>{' '}
              {t('pagination.results', 'results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pagination.previous', 'Previous')}
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {page}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(total / limit)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pagination.next', 'Next')}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
